/* ═══════════════════════════════════════════════════════════════════════════════
   Safe Mode Tour (v2.6.0)
   
   Feature update tour for the Safe Mode (presentation privacy) system:
   - Toggle in sidebar (ShieldCheck icon)
   - Keyboard shortcut: ⌘⇧S (Mac) / Ctrl+Shift+S (Windows)
   - Masks ALL dollar amounts app-wide with blurred placeholders
   - Non-financial data (patching ratios, chart shapes, project rankings) stays visible
   - Persists across page refreshes via localStorage
   
   Designed for screen-sharing with builders, subcontractors, or clients.
   
   Pages: 'dashboard' (sidebar always visible)
   ═══════════════════════════════════════════════════════════════════════════════ */

import type { TourDefinition } from './types';

export const safeModeTour: TourDefinition = {
  id: 'safe_mode_v2_6',
  version: '2.6.0',
  type: 'feature_update',
  audience: 'all',
  page: 'dashboard',
  priority: 3,
  title: 'Safe Mode',
  subtitle: 'Hide sensitive financial data when presenting or screen-sharing.',

  steps: [
    // ── Step 0: Welcome ──────────────────────────────────────────────
    {
      id: 'welcome',
      type: 'welcome',
      title: 'Introducing Safe Mode',
      description: 'Sharing your screen with a builder or client? Safe Mode masks all dollar amounts while keeping non-financial data like patching ratios and project rankings visible, so you can still discuss progress without revealing financials.',
      icon: '🛡️',
      actionLabel: 'Show Me',
    },

    // ── Step 1: Sidebar Toggle ───────────────────────────────────────
    {
      id: 'safe-mode-toggle',
      type: 'spotlight',
      targetSelector: '[data-tour-id="sidebar-safe-mode-toggle"]',
      position: 'right',
      title: 'Safe Mode Toggle',
      description: 'Click this button to activate Safe Mode. When on, every dollar figure is replaced with a masked placeholder (•••••), financial charts are hidden, and the P&L tab is removed. Only the Patching Analysis stays visible so you can discuss defect data without exposing financials.',
      icon: '🔒',
      actionLabel: 'Next',
      spotlightPadding: 6,
    },

    // ── Step 2: Keyboard Shortcut ────────────────────────────────────
    {
      id: 'keyboard-shortcut',
      type: 'info_card',
      title: 'Quick Toggle: ⌘⇧S',
      description: 'Press ⌘ + Shift + S (Mac) or Ctrl + Shift + S (Windows) from any page to instantly toggle Safe Mode, with no need to find the sidebar button. Note: this is different from Win + Shift + S which opens the Windows Snipping Tool.',
      icon: '⌨️',
      actionLabel: 'Got It',
    },

    // ── Step 3: Completion ───────────────────────────────────────────
    {
      id: 'completion',
      type: 'completion',
      title: 'You\'re Protected!',
      description: 'Enable Safe Mode before any presentation or screen-share. It stays active even after page refreshes until you toggle it off.',
      icon: '🎉',
      actionLabel: 'Start Exploring',
    },
  ],
};
